import { useEffect, useState } from "react";
import { Users, Copy, UserPlus, Heart, Trophy, Flame, Send, Trash2, PartyPopper, ClipboardList, Sparkles, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader, Bullets, SectionCard } from "@/components/common";
import { Switch } from "@/components/ui/switch";

export default function Family() {
  const [me, setMe] = useState(null);
  const [code, setCode] = useState("");
  const [supporting, setSupporting] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [cheers, setCheers] = useState([]);
  const [cheerFor, setCheerFor] = useState(null);
  const [cheerMsg, setCheerMsg] = useState("");
  const [digestFor, setDigestFor] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);

  const load = () => {
    api.get("/circle/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/circle/supporting").then((r) => setSupporting(r.data.items)).catch(() => {});
    api.get("/circle/supporters").then((r) => setSupporters(r.data.items)).catch(() => {});
    api.get("/circle/cheers").then((r) => setCheers(r.data.items)).catch(() => {});
  };
  useEffect(load, []);

  const connect = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/circle/connect", { code: code.trim() });
      toast.success(`Connected with ${data.learner_name} — you'll see their progress`);
      setCode(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect"); }
  };

  const disconnect = async (id) => { await api.delete(`/circle/${id}`); load(); };

  const toggleRemind = async (learner, val) => {
    setSupporting((s) => s.map((x) => (x.id === learner.id ? { ...x, remind: val, nudge: val && !x.last_cheer_at ? true : x.nudge } : x)));
    try { await api.put("/circle/remind", { learner_id: learner.id, remind: val }); load(); } catch { toast.error("Could not update reminder"); }
  };

  const sendCheer = async (learner) => {
    if (!cheerMsg.trim()) return;
    try {
      await api.post("/circle/cheer", { to_user: learner.id, message: cheerMsg.trim() });
      toast.success(`Cheer sent to ${learner.name} 🎉`);
      setCheerFor(null); setCheerMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };

  const copyCode = () => { navigator.clipboard?.writeText(me?.code || ""); toast.success("Code copied"); };

  const loadDigest = async (learner) => {
    if (digestFor === learner.id) { setDigestFor(null); return; }
    setDigestFor(learner.id); setDigest(null); setDigestLoading(true);
    try {
      const { data } = await api.get(`/circle/digest/${learner.id}`);
      setDigest(data);
    } catch { toast.error("Could not load digest."); setDigestFor(null); }
    finally { setDigestLoading(false); }
  };

  const applySuggestedCheer = (learner) => {
    setCheerFor(learner.id);
    setCheerMsg(digest?.suggested_cheer || "");
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl flex items-center gap-3"><Users className="w-8 h-8 text-primary" />Family & School</h1>
        <p className="text-muted-foreground mt-2">Let a parent, teacher or family member follow your learning and cheer you on. Sharing is always your choice.</p>
      </div>

      {/* Invite code */}
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-5 sm:p-6" data-testid="invite-card">
        <h2 className="font-head font-bold text-lg">Your connect code</h2>
        <p className="text-sm text-muted-foreground mt-1">Share this with someone you trust so they can support your progress.</p>
        <div className="flex items-center gap-2 mt-3">
          <code className="flex-1 rounded-md border border-border bg-card px-3 py-2.5 font-mono text-sm break-all" data-testid="my-code">{me?.code || "…"}</code>
          <button onClick={copyCode} data-testid="copy-code" className="h-11 px-4 grid place-items-center rounded-md bg-primary text-primary-foreground font-bold hover:brightness-110"><Copy className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Connect */}
      <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />Support someone's learning</h2>
        <p className="text-sm text-muted-foreground mt-1">Enter the connect code they shared with you.</p>
        <div className="flex gap-2 mt-3">
          <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="connect-input" placeholder="Paste a connect code…" className="flex-1 rounded-md border border-input bg-background px-3 py-2.5 outline-none focus:ring-2 focus:ring-ring" />
          <button onClick={connect} data-testid="connect-btn" className="rounded-md bg-primary text-primary-foreground px-5 font-bold hover:brightness-110">Connect</button>
        </div>
      </div>

      {/* Learners I support */}
      {supporting.length > 0 && (
        <div>
          <h2 className="font-head font-bold text-lg mb-3">People you're supporting</h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="supporting-list">
            {supporting.map((l) => (
              <div key={l.id} className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-head font-bold text-lg">{l.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{l.mode} mode · {l.relation}</div>
                  </div>
                  <button onClick={() => disconnect(l.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="rounded-md bg-secondary p-2"><div className="inline-flex items-center gap-1 font-bold"><Trophy className="w-3.5 h-3.5 text-primary" />Lv{l.level}</div><div className="text-[10px] text-muted-foreground">{l.xp} XP</div></div>
                  <div className="rounded-md bg-secondary p-2"><div className="inline-flex items-center gap-1 font-bold"><Flame className="w-3.5 h-3.5 text-primary" />{l.streak}</div><div className="text-[10px] text-muted-foreground">streak</div></div>
                  <div className="rounded-md bg-secondary p-2"><div className="font-bold">{l.completed_skills}</div><div className="text-[10px] text-muted-foreground">skills</div></div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">{l.goals_done}/{l.goals_total} goals done</div>
                {l.nudge && (
                  <div className="mt-3 flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm" data-testid={`nudge-${l.id}`}>
                    <BellRing className="w-4 h-4 text-primary shrink-0" /> Time for a weekly check-in with {l.name}!
                  </div>
                )}
                <div className="flex items-center justify-between mt-3 text-sm">
                  <span className="inline-flex items-center gap-2 text-muted-foreground"><BellRing className="w-4 h-4" />Weekly reminder</span>
                  <Switch checked={!!l.remind} onCheckedChange={(v) => toggleRemind(l, v)} data-testid={`remind-${l.id}`} />
                </div>
                {cheerFor === l.id ? (
                  <div className="flex gap-2 mt-3">
                    <input value={cheerMsg} onChange={(e) => setCheerMsg(e.target.value)} autoFocus data-testid="cheer-input" placeholder="Write encouragement…" className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
                    <button onClick={() => sendCheer(l)} data-testid="cheer-send" className="rounded-md bg-primary text-primary-foreground px-3 grid place-items-center"><Send className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => { setCheerFor(l.id); setCheerMsg(""); }} data-testid={`cheer-${l.id}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary text-primary font-bold py-2 hover:bg-primary hover:text-primary-foreground transition">
                    <Heart className="w-4 h-4" /> Send a cheer
                  </button>
                )}
                <button onClick={() => loadDigest(l)} data-testid={`digest-${l.id}`} className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full border border-border py-2 text-sm font-semibold hover:bg-secondary">
                  <ClipboardList className="w-4 h-4" /> {digestFor === l.id ? "Hide" : "Weekly"} digest
                </button>
                {digestFor === l.id && (
                  <div className="mt-3 space-y-3" data-testid="digest-panel">
                    {digestLoading && <Loader label="Summarising the week…" />}
                    {digest && (
                      <>
                        {digest.summary && <p className="text-sm leading-relaxed">{digest.summary}</p>}
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="rounded-md bg-secondary p-2"><div className="font-bold">{digest.stats?.skills_this_week ?? 0}</div><div className="text-[10px] text-muted-foreground">skills</div></div>
                          <div className="rounded-md bg-secondary p-2"><div className="font-bold">{digest.stats?.goals_done_this_week ?? 0}</div><div className="text-[10px] text-muted-foreground">goals</div></div>
                          <div className="rounded-md bg-secondary p-2"><div className="font-bold">{digest.stats?.questions_asked_this_week ?? 0}</div><div className="text-[10px] text-muted-foreground">questions</div></div>
                        </div>
                        {digest.highlights?.length > 0 && <Bullets items={digest.highlights} />}
                        {digest.suggested_cheer && (
                          <button onClick={() => applySuggestedCheer(l)} data-testid="digest-use-cheer" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary/10 text-primary border border-primary/40 font-semibold py-2 text-sm hover:bg-primary hover:text-primary-foreground transition">
                            <Sparkles className="w-4 h-4" /> Send: "{digest.suggested_cheer.slice(0, 40)}…"
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cheers received */}
      <div>
        <h2 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><PartyPopper className="w-5 h-5 text-primary" />Cheers for you</h2>
        {supporters.length > 0 && <p className="text-sm text-muted-foreground mb-3">{supporters.map((s) => s.name).join(", ")} {supporters.length === 1 ? "is" : "are"} supporting you.</p>}
        {cheers.length === 0 ? (
          <p className="text-muted-foreground text-sm">No cheers yet. Share your connect code to get encouragement from family or teachers.</p>
        ) : (
          <div className="space-y-2" data-testid="cheers-list">
            {cheers.map((c) => (
              <div key={c.id} className="rounded-lg border border-border bg-card p-4 flex items-start gap-3">
                <Heart className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div><p>{c.message}</p><p className="text-xs text-muted-foreground mt-1">from {c.from_name} · {new Date(c.created_at).toLocaleDateString()}</p></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
